import React, { memo } from 'react';
import styled from 'styled-components';
import PredictionChartRow from './PredictionChartRow';
import KubitTooltip from '../../KubitTooltip';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import { getScrollBarStyles } from '../../../config/styles';
import { useChartContext } from '../ChartContext';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { theme } from '../../../styles/theme';

const PredictionContainer = styled.div`
  max-width: 100%;
  overflow-x: auto;
`;

const PredictionTableHeader = styled.div`
  height: 48px;
  background-color: ${theme.colors.tableHeader};
  display: flex;
  border: 1px solid ${theme.colors.border};
`;

const EventLeftContainer = styled(({ ...props }) => <div {...props} />)`
  width: fit-content;
  display: grid;
`;
const EventTitle = styled(({ isEvent, ...props }) => <div {...props} />)`
  width: 250px;
  height: 48px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${({ isEvent }) =>
    isEvent ? theme.colors.level1 : theme.colors.tableHeader};
  color: ${theme.colors.secondary900};
  border-right: 1px solid ${theme.colors.border};
  border-left: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  border-top: ${({ isEvent }) =>
    isEvent ? `1px solid ${theme.colors.border}` : 0};
  padding: 14px 8px;
`;

const TitleTriggerText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: fit-content;
  max-width: 234px;
`;

const HeaderColumn = styled.span`
  width: 95px;
  min-width: 95px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: #8a94bd;
  padding: 14px 8px;
  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.border};
  }
`;

const TableContent = styled.div`
  display: flex;
`;

const EventRightContainer = styled.div`
  overflow-y: hidden;
  border-right: 1px solid ${theme.colors.border};

  ${getScrollBarStyles(4)}
`;
const HeaderOverflowBar = styled.div`
  overflow-y: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;
export default memo(({ analysisId }: any) => {
  const {
    chartData,
    settings: { chartWidth },
  } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Prediction);
  const { events, correlationData } = chartData.data;
  const firstEvent = events.length > 0 ? events[0] : null;
  const predictionDays = firstEvent ? correlationData[firstEvent] : [];
  const predictionWidth = Math.min(
    chartWidth - 36,
    250 + predictionDays.length * 95 + 1,
  );

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <div onContextMenu={handleContextMenu}>
      <ScrollSync>
        <PredictionContainer style={{ width: predictionWidth }}>
          <PredictionTableHeader>
            <EventTitle
              style={{
                borderLeft: 'none',
                borderBottom: 'none',
                width: '249px',
                minWidth: '249px',
              }}
            >
              Event Name
            </EventTitle>
            <ScrollSyncPane>
              <HeaderOverflowBar>
                <div style={{ display: 'flex' }}>
                  {predictionDays.map((_: any, index: number) => {
                    const dayName = index === 0 ? '1 day' : `${index + 1} days`;
                    if (index === 0) {
                      return (
                        <HeaderColumn key={`day-${index}`}>
                          <span
                            style={{ fontWeight: 500, color: '#c4c9de' }}
                          >{`within `}</span>
                          {dayName}
                        </HeaderColumn>
                      );
                    }
                    return (
                      <HeaderColumn key={`day-${index}`}>
                        {dayName}
                      </HeaderColumn>
                    );
                  })}
                </div>
              </HeaderOverflowBar>
            </ScrollSyncPane>
          </PredictionTableHeader>
          <TableContent>
            <EventLeftContainer>
              {events.map((event: string, index: number) => {
                return (
                  <EventTitle isEvent={index % 2 === 0} key={`event-${index}`}>
                    <KubitTooltip
                      content={event}
                      placement="bottom"
                      noOpenDelay={true}
                    >
                      <TitleTriggerText>{event}</TitleTriggerText>
                    </KubitTooltip>
                  </EventTitle>
                );
              })}
            </EventLeftContainer>
            <ScrollSyncPane>
              <EventRightContainer>
                {events.map((event: string, index: number) => {
                  return (
                    <PredictionChartRow
                      key={`cellRow-${index}`}
                      rowIndex={index}
                      chartData={chartData.data}
                      analysisId={analysisId}
                      event={event}
                    />
                  );
                })}
              </EventRightContainer>
            </ScrollSyncPane>
          </TableContent>
        </PredictionContainer>
      </ScrollSync>
    </div>
  );
});
